'use client'

import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { getPostLoginPath } from '@/services/domain/auth/SessionService'
import { IRegisterCompanyForm, IRegisterPersonForm } from '~/schemas/auth/authFormSchemas'
import { IRegisterResponse } from '~/schemas/auth/authSchemas'
import { registerCompanyService, registerPersonService } from '~/services/domain/auth/RegisterService'

/**
 * Cadastro PF e PJ. Abre a sessão (a API já passou o carrinho anônimo para o cliente) e leva ao `redirect` ou à home.
 * Rejeita com o ApiError sem aviso: a tela usa o useFormAction com os mapas de campo acima.
 */
export function useRegister() {
    const router = useRouter()

    const onRegistered = (session: IRegisterResponse, redirect?: string) => {
        toast.success('Cadastro realizado com sucesso!', { autoClose: 2000 })
        router.push(getPostLoginPath(session, redirect))
    }

    const personMutation = useMutation({
        mutationFn: ({ form }: { form: IRegisterPersonForm; redirect?: string }) => registerPersonService(form),
        onSuccess: (session, { redirect }) => onRegistered(session, redirect),
    })

    const companyMutation = useMutation({
        mutationFn: ({ form }: { form: IRegisterCompanyForm; redirect?: string }) => registerCompanyService(form),
        onSuccess: (session, { redirect }) => onRegistered(session, redirect),
    })

    const registerPerson = useCallback(
        (form: IRegisterPersonForm, redirect?: string) => personMutation.mutateAsync({ form, redirect }),
        [personMutation.mutateAsync],
    )

    const registerCompany = useCallback(
        (form: IRegisterCompanyForm, redirect?: string) => companyMutation.mutateAsync({ form, redirect }),
        [companyMutation.mutateAsync],
    )

    return {
        registerPerson,
        registerCompany,
        isLoading: personMutation.isPending || companyMutation.isPending,
    }
}
